// مكتبة الأجهزة الكهربائية: مواصفات كل جهاز (الأطراف، النقاط، الملف، الأحمال)
export type Rail = "L1" | "L2" | "L3" | "N" | "PE" | "C+" | "C-";
export type Side = "top" | "bottom" | "left" | "right";
export type Actuator =
  | "coil"
  | "manual"
  | "press"
  | "trip"
  | "select"
  | "link"
  | "none";

export interface TerminalSpec {
  id: string;
  label: string;
  side: Side;
  slot: number;
  slots: number;
  role: "phase" | "neutral" | "earth" | "control" | "load";
}

export interface PoleSpec {
  a: string;
  b: string;
  type: "no" | "nc";
  main?: boolean;

  /** مواضع السلكتر التي تكون فيها هذه النقطة مغلقة */
  pos?: number[];
}

export interface DeviceSpec {
  type: string;
  name: string;
  group: string;
  w: number;
  h: number;

  /** نمط الرسم */
  body: string;

  actuator: Actuator;

  defaultClosed?: boolean;

  terminals: TerminalSpec[];

  poles?: PoleSpec[];

  /** وصلات داخلية دائمة */
  links?: [string, string][];

  coil?: [string, string];

  coilVoltage?: string;

  /** فولتية المصدر الافتراضية القابلة للتعديل من الخصائص */
  sourceVoltage?: number;

  /** إعدادات حماية الفولتية / فقدان الفاز */
  protectionMinVoltage?: number;
  protectionMaxVoltage?: number;
  /** زمن انتظار خرج الحماية بالثواني */
  protectionDelay?: number;

  /** للتايمر بالثواني */
  delay?: number;

  /** تأخير عمل / تأخير فصل */
  timerMode?: "on" | "off";

  /** نقاط مساعدة يمكن ربطها بعنصر آخر */
  linkable?: boolean;

  /** أسماء مواضع مفتاح الاختيار */
  positions?: string[];

  rails?: {
    t: string;
    rail: Rail;
  }[];

  load?: {
    kind: "3ph" | "1ph" | "dc";
    terminals: string[];
    visual: string;
  };

  meter?: "volt" | "amp" | "freq" | "multi";

  info?: string;
}

const T = (
  ids: string[],
  side: Side,
  role: TerminalSpec["role"],
  labels?: string[],
): TerminalSpec[] =>
  ids.map((id, i) => ({
    id,
    label: labels?.[i] ?? id,
    side,
    slot: i,
    slots: ids.length,
    role,
  }));

export const GROUPS: {
  id: string;
  name: string;
}[] = [
  {
    id: "contactors",
    name: "الكونتكترات",
  },
  {
    id: "aux",
    name: "النقاط المساعدة (تابعة لعنصر)",
  },
  {
    id: "relays",
    name: "الريليهات والتايمرات",
  },
  {
    id: "buttons",
    name: "الأزرار Push Buttons",
  },
  {
    id: "switches",
    name: "مفاتيح الاختيار Selector",
  },
  {
    id: "sensors",
    name: "مفاتيح الحساسات",
  },
  {
    id: "breakers",
    name: "القواطع MCB / MCCB / MPCB",
  },
  {
    id: "rccb",
    name: "RCCB / RCBO",
  },
  {
    id: "overload",
    name: "الأوفرلود والفيوزات",
  },
  {
    id: "phase",
    name: "حماية الفازات والفولتية",
  },
  {
    id: "terminals",
    name: "الترمينلات والباسبار",
  },
  {
    id: "dist",
    name: "التوزيع والعزل",
  },
  {
    id: "meters",
    name: "أجهزة القياس والإشارة",
  },
  {
    id: "sources",
    name: "مصادر التغذية",
  },
  {
    id: "loads",
    name: "المحركات والأحمال",
  },
];

const specs: DeviceSpec[] = [
  // =====================================================
  // الكونتكترات
  // =====================================================

  {
    type: "contactor",
    name: "كونتكتر ثلاثي",
    group: "contactors",

    // صورة Schneider TeSys LC1D25 الجديدة المرسلة من المستخدم.
    // نحافظ على نسبة الصورة الأصلية بعد إزالة الخلفية.
    w: 174,
    h: 301,

    body: "contactor",

    actuator: "coil",

    coil: ["A1", "A2"],

    coilVoltage: "220V AC",

    terminals: [
      ...T(["L1", "L2", "L3"], "top", "phase"),
      ...T(["T1", "T2", "T3"], "bottom", "phase"),
      ...T(["A1", "13", "21"], "left", "control"),
      ...T(["A2", "14", "22"], "right", "control"),
    ],

    poles: [
      {
        a: "L1",
        b: "T1",
        type: "no",
        main: true,
      },
      {
        a: "L2",
        b: "T2",
        type: "no",
        main: true,
      },
      {
        a: "L3",
        b: "T3",
        type: "no",
        main: true,
      },
      {
        a: "13",
        b: "14",
        type: "no",
      },
      {
        a: "21",
        b: "22",
        type: "nc",
      },
    ],

    info:
      "ملف A1/A2 – نقاط رئيسية L→T – مساعد 13/14 NO و21/22 NC",
  },

  {
    type: "contactor_small",
    name: "كونتكتر رباعي",
    group: "contactors",

    // Schneider TeSys LC1DT40 الرباعي الجديد المرسل من المستخدم.
    // نحافظ على نسبة الصورة الأصلية بعد إزالة الخلفية.
    w: 174,
    h: 359,

    body: "contactor",
    actuator: "coil",
    coil: ["A1", "A2"],
    coilVoltage: "220V AC",

    terminals: [
      ...T(["L1", "L2", "L3", "L4"], "top", "phase"),
      ...T(["T1", "T2", "T3", "T4"], "bottom", "phase"),
      ...T(["A1", "13", "21"], "left", "control"),
      ...T(["A2", "14", "22"], "right", "control"),
    ],

    poles: [
      { a: "L1", b: "T1", type: "no", main: true },
      { a: "L2", b: "T2", type: "no", main: true },
      { a: "L3", b: "T3", type: "no", main: true },
      { a: "L4", b: "T4", type: "no", main: true },
      { a: "13", b: "14", type: "no" },
      { a: "21", b: "22", type: "nc" },
    ],

    info:
      "كونتكتر رباعي LC1DT40 – أربع نقاط رئيسية L→T – مساعد 13/14 NO و21/22 NC – ملف A1/A2",
  },

  // =====================================================
  // الريليهات والتايمرات
  // =====================================================

  {
    type: "relay",
    name: "ريليه Schneider RXM 14 Pin",
    group: "relays",

    w: 150,
    h: 190,
    body: "relay",
    actuator: "coil",
    coil: ["13", "14"],

    terminals: [
      ...T(["1", "2", "3", "4", "5", "6", "7"], "top", "control"),
      ...T(["8", "9", "10", "11", "12", "13", "14"], "bottom", "control"),
    ],

    poles: [
      { a: "9", b: "1", type: "nc" }, { a: "9", b: "5", type: "no" },
      { a: "10", b: "2", type: "nc" }, { a: "10", b: "6", type: "no" },
      { a: "11", b: "3", type: "nc" }, { a: "11", b: "7", type: "no" },
      { a: "12", b: "4", type: "nc" }, { a: "12", b: "8", type: "no" },
    ],

    info: "RXM 14-pin: coil 13/14; COM 9→NC1/NO5, COM10→NC2/NO6, COM11→NC3/NO7, COM12→NC4/NO8",
  },

  {
    type: "timer",
    name: "تايمر تأخير عمل ON-Delay",
    group: "relays",

    // صورة PF083A أطول من التايمر القديم، لذلك نحافظ على نسبة قريبة من الأصل.
    w: 155,
    h: 170,

    body: "timer",
    actuator: "coil",

    // حسب تحديدك: 2 = A1 و 7 = A2
    coil: ["2", "7"],
    coilVoltage: "220V AC",

    delay: 3,
    timerMode: "on",

    // ترتيب الأطراف مطابق للصورة:
    // فوق: 6 - 5 - 4 - 3
    // جوه: 7 - 8 - 1 - 2
    terminals: [
      ...T(["6", "5", "4", "3"], "top", "control"),
      ...T(["7", "8", "1", "2"], "bottom", "control"),
    ],

    // 1 COM: 1-4 NC / 1-3 NO
    // 8 COM: 8-5 NC / 8-6 NO
    poles: [
      { a: "1", b: "4", type: "nc" },
      { a: "1", b: "3", type: "no" },
      { a: "8", b: "5", type: "nc" },
      { a: "8", b: "6", type: "no" },
    ],

    info:
      "PF083A: Coil 2/7 (A1/A2) – COM 1→NC4/NO3 – COM 8→NC5/NO6 – ON-Delay",
  },

  {
    type: "timer_off",
    name: "تايمر تأخير فصل OFF-Delay",
    group: "relays",

    w: 155,
    h: 170,

    body: "timer",
    actuator: "coil",

    // نفس قاعدة PF083A ونفس أطراف التغذية.
    coil: ["2", "7"],
    coilVoltage: "220V AC",

    delay: 3,
    timerMode: "off",

    terminals: [
      ...T(["6", "5", "4", "3"], "top", "control"),
      ...T(["7", "8", "1", "2"], "bottom", "control"),
    ],

    poles: [
      { a: "1", b: "4", type: "nc" },
      { a: "1", b: "3", type: "no" },
      { a: "8", b: "5", type: "nc" },
      { a: "8", b: "6", type: "no" },
    ],

    info:
      " تايمر Coil 2/7 (A1/A2) – COM 1→NC4/NO3 – COM 8→NC5/NO6",
  },

 
  // =====================================================
  // النقاط المساعدة
  // =====================================================

  {
    type: "aux_block",
    name: "أوكسيلاري LADN13",
    group: "aux",

    w: 160,
    h: 160,

    body: "aux",

    actuator: "link",

    linkable: true,

    // مطابق للصورة LADN13: 53/54 NO و61/62 و71/72 و81/82 NC.
    terminals: [
      ...T(["53", "61", "71", "81"], "top", "control"),
      ...T(["54", "62", "72", "82"], "bottom", "control"),
    ],

    poles: [
      { a: "53", b: "54", type: "no" },
      { a: "61", b: "62", type: "nc" },
      { a: "71", b: "72", type: "nc" },
      { a: "81", b: "82", type: "nc" },
    ],

    info:
      "أوكسيلاري Schneider LADN13: نقطة 53/54 NO وثلاث نقاط NC: 61/62 و71/72 و81/82",
  },

  // =====================================================
  // PUSH BUTTONS
  // =====================================================

  {
    type: "pb_start",
    name: "زر تشغيل START (NO) لحظي",
    group: "buttons",

    w: 100,
    h: 100,

    body: "pb_green",

    actuator: "press",

    terminals: [
      ...T(["3"], "top", "control"),
      ...T(["4"], "bottom", "control"),
    ],

    poles: [
      {
        a: "3",
        b: "4",
        type: "no",
      },
    ],

    info:
      "يغلق أثناء الضغط فقط ويرجع فوراً عند رفع الإصبع",
  },

  {
    type: "pb_stop",
    name: "زر إيقاف STOP (NC) لحظي",
    group: "buttons",

    w: 100,
    h: 100,

    body: "pb_red",

    actuator: "press",

    terminals: [
      ...T(["1"], "top", "control"),
      ...T(["2"], "bottom", "control"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "nc",
      },
    ],

    info:
      "مغلق في الوضع الطبيعي ويفتح أثناء الضغط فقط",
  },

  {
    type: "pb_no",
    name: "Push Button NO لحظي",
    group: "buttons",

    w: 100,
    h: 100,

    body: "pb_black",

    actuator: "press",

    terminals: [
      ...T(["3"], "top", "control"),
      ...T(["4"], "bottom", "control"),
    ],

    poles: [
      {
        a: "3",
        b: "4",
        type: "no",
      },
    ],

    info:
      "يغلق أثناء الضغط فقط",
  },

  {
    type: "pb_nc",
    name: "Push Button NC لحظي",
    group: "buttons",

    w: 100,
    h: 100,

    body: "pb_black",

    actuator: "press",

    terminals: [
      ...T(["1"], "top", "control"),
      ...T(["2"], "bottom", "control"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "nc",
      },
    ],

    info:
      "يفتح أثناء الضغط فقط",
  },

  {
    type: "pb_no_nc",
    name: "Push Button NO + NC لحظي",
    group: "buttons",

    w: 120,
    h: 105,

    body: "pb_black",

    actuator: "press",

    terminals: [
      ...T(["3", "1"], "top", "control"),
      ...T(["4", "2"], "bottom", "control"),
    ],

    poles: [
      {
        a: "3",
        b: "4",
        type: "no",
      },
      {
        a: "1",
        b: "2",
        type: "nc",
      },
    ],

    info:
      "نقطة NO 3/4 ونقطة NC 1/2 تعملان معاً أثناء الضغط",
  },

  {
    type: "pb_lit_green",
    name: "زر مضيء أخضر (NO + مصباح)",
    group: "buttons",

    w: 120,
    h: 110,

    body: "pb_green",

    actuator: "press",

    terminals: [
      ...T(["3", "X1"], "top", "control"),
      ...T(["4", "X2"], "bottom", "control"),
    ],

    poles: [
      {
        a: "3",
        b: "4",
        type: "no",
      },
    ],

    load: {
      kind: "1ph",
      terminals: ["X1", "X2"],
      visual: "lamp",
    },

    info:
      "نقطة NO 3/4 لحظية + مصباح داخلي X1/X2 يضيء عند تغذيته",
  },

  {
    type: "pb_lit_red",
    name: "زر مضيء أحمر (NC + مصباح)",
    group: "buttons",

    w: 120,
    h: 110,

    body: "pb_red",

    actuator: "press",

    terminals: [
      ...T(["1", "X1"], "top", "control"),
      ...T(["2", "X2"], "bottom", "control"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "nc",
      },
    ],

    load: {
      kind: "1ph",
      terminals: ["X1", "X2"],
      visual: "lamp",
    },

    info:
      "نقطة NC 1/2 لحظية + مصباح داخلي X1/X2",
  },

  {
    type: "estop",
    name: "Emergency Stop (قفل وتحرير)",
    group: "buttons",

    w: 110,
    h: 110,

    body: "estop",

    actuator: "manual",

    terminals: [
      ...T(["1"], "top", "control"),
      ...T(["2"], "bottom", "control"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "nc",
      },
    ],

    info:
      "اضغط ليقفل الدائرة ويبقى مضغوطاً، واضغط مرة أخرى (افتل) للتحرير",
  },

  // =====================================================
  // SELECTORS
  // =====================================================

  {
    type: "sel_on_off",
    name: "Selector ON / OFF",
    group: "switches",

    w: 110,
    h: 100,

    body: "selector",

    actuator: "select",

    positions: ["OFF", "ON"],

    terminals: [
      ...T(["1"], "top", "control"),
      ...T(["2"], "bottom", "control"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "no",
        pos: [1],
      },
    ],

    info:
      "يبقى ثابتاً على الوضعية المختارة: 1/2 مغلقة في ON",
  },

  {
    type: "sel_0_1",
    name: "Selector 0 / 1",
    group: "switches",

    w: 110,
    h: 100,

    body: "selector",

    actuator: "select",

    positions: ["0", "1"],

    terminals: [
      ...T(["1"], "top", "control"),
      ...T(["2"], "bottom", "control"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "no",
        pos: [1],
      },
    ],
  },

  {
    type: "sel_off_on_on",
    name: "Selector OFF / ON / ON",
    group: "switches",

    w: 130,
    h: 105,

    body: "selector",

    actuator: "select",

    positions: ["OFF", "ON 1", "ON 2"],

    terminals: [
      ...T(["1", "3"], "top", "control"),
      ...T(["2", "4"], "bottom", "control"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "no",
        pos: [1],
      },
      {
        a: "3",
        b: "4",
        type: "no",
        pos: [2],
      },
    ],
  },

  {
    type: "sel_0_1_2",
    name: "Selector 0 / 1 / 2",
    group: "switches",

    w: 130,
    h: 105,

    body: "selector",

    actuator: "select",

    positions: ["0", "1", "2"],

    terminals: [
      ...T(["1", "3"], "top", "control"),
      ...T(["2", "4"], "bottom", "control"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "no",
        pos: [1],
      },
      {
        a: "3",
        b: "4",
        type: "no",
        pos: [2],
      },
    ],
  },

  {
    type: "sel_hand_off_auto",
    name: "Selector HAND / OFF / AUTO",
    group: "switches",

    w: 140,
    h: 110,

    body: "selector",

    actuator: "select",

    positions: ["HAND", "OFF", "AUTO"],

    terminals: [
      ...T(["1", "3"], "top", "control"),
      ...T(["2", "4"], "bottom", "control"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "no",
        pos: [0],
      },
      {
        a: "3",
        b: "4",
        type: "no",
        pos: [2],
      },
    ],

    info:
      "1/2 مغلقة في HAND – 3/4 مغلقة في AUTO – الوسط OFF الكل مفتوح",
  },

  {
    type: "sel_man_off_auto",
    name: "Selector MANUAL / OFF / AUTO",
    group: "switches",

    w: 145,
    h: 110,

    body: "selector",

    actuator: "select",

    positions: ["MANUAL", "OFF", "AUTO"],

    terminals: [
      ...T(["1", "3"], "top", "control"),
      ...T(["2", "4"], "bottom", "control"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "no",
        pos: [0],
      },
      {
        a: "3",
        b: "4",
        type: "no",
        pos: [2],
      },
    ],
  },

  {
    type: "sel_fwd_off_rev",
    name: "Selector Forward / OFF / Reverse",
    group: "switches",

    w: 150,
    h: 110,

    body: "selector",

    actuator: "select",

    positions: ["FORWARD", "OFF", "REVERSE"],

    terminals: [
      ...T(["1", "3"], "top", "control"),
      ...T(["2", "4"], "bottom", "control"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "no",
        pos: [0],
      },
      {
        a: "3",
        b: "4",
        type: "no",
        pos: [2],
      },
    ],

    info:
      "1/2 للأمام – 3/4 للخلف – لا يمكن غلق الاتجاهين معاً",
  },

  {
    type: "key_switch",
    name: "مفتاح مفتاحي Key Switch",
    group: "switches",

    w: 115,
    h: 100,

    body: "selector",

    actuator: "select",

    positions: ["OFF", "ON"],

    terminals: [
      ...T(["1"], "top", "control"),
      ...T(["2"], "bottom", "control"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "no",
        pos: [1],
      },
    ],
  },

  // =====================================================
  // SENSORS
  // =====================================================

  {
    type: "limit_switch",
    name: "Limit Switch",
    group: "sensors",

    w: 120,
    h: 100,

    body: "limit",

    actuator: "manual",

    terminals: [
      ...T(["11"], "left", "control", ["COM"]),
      ...T(
        ["14", "12"],
        "right",
        "control",
        ["NO 14", "NC 12"],
      ),
    ],

    poles: [
      {
        a: "11",
        b: "14",
        type: "no",
      },
      {
        a: "11",
        b: "12",
        type: "nc",
      },
    ],

    info:
      "اضغط عليه أثناء المحاكاة لمحاكاة وصول الحركة (يبقى بالوضعية)",
  },

  {
    type: "foot_switch",
    name: "Foot Switch دعسة",
    group: "sensors",

    w: 130,
    h: 100,

    body: "limit",

    actuator: "press",

    terminals: [
      ...T(["11"], "left", "control", ["COM"]),
      ...T(
        ["14", "12"],
        "right",
        "control",
        ["NO 14", "NC 12"],
      ),
    ],

    poles: [
      {
        a: "11",
        b: "14",
        type: "no",
      },
      {
        a: "11",
        b: "12",
        type: "nc",
      },
    ],

    info:
      "لحظي: يعمل أثناء الدعس فقط",
  },

  {
    type: "float_switch",
    name: "Float Switch عوّامة",
    group: "sensors",

    w: 120,
    h: 110,

    body: "limit",

    actuator: "manual",

    terminals: [
      ...T(["11"], "left", "control", ["COM"]),
      ...T(
        ["14", "12"],
        "right",
        "control",
        ["NO 14", "NC 12"],
      ),
    ],

    poles: [
      {
        a: "11",
        b: "14",
        type: "no",
      },
      {
        a: "11",
        b: "12",
        type: "nc",
      },
    ],

    info:
      "اضغط لمحاكاة امتلاء الخزان",
  },

  {
    type: "pressure_switch",
    name: "Pressure Switch ضغط",
    group: "sensors",

    w: 125,
    h: 110,

    body: "limit",

    actuator: "manual",

    terminals: [
      ...T(["11"], "left", "control", ["COM"]),
      ...T(
        ["14", "12"],
        "right",
        "control",
        ["NO 14", "NC 12"],
      ),
    ],

    poles: [
      {
        a: "11",
        b: "14",
        type: "no",
      },
      {
        a: "11",
        b: "12",
        type: "nc",
      },
    ],
  },

  {
    type: "thermostat",
    name: "Thermostat ثرموستات",
    group: "sensors",

    w: 125,
    h: 110,

    body: "limit",

    actuator: "manual",

    terminals: [
      ...T(["11"], "left", "control", ["COM"]),
      ...T(
        ["14", "12"],
        "right",
        "control",
        ["NO 14", "NC 12"],
      ),
    ],

    poles: [
      {
        a: "11",
        b: "14",
        type: "no",
      },
      {
        a: "11",
        b: "12",
        type: "nc",
      },
    ],
  },

  {
    type: "sensor_npn",
    name: "نقطة حسّاس اقتراب NO",
    group: "sensors",

    w: 115,
    h: 100,

    body: "limit",

    actuator: "manual",

    terminals: [
      ...T(["3"], "top", "control"),
      ...T(["4"], "bottom", "control"),
    ],

    poles: [
      {
        a: "3",
        b: "4",
        type: "no",
      },
    ],
  },

  // =====================================================
  // BREAKERS
  // =====================================================

  {
    type: "mcb_1p",
    name: "MCB أحادي الطور",
    group: "breakers",

    w: 80,
    h: 130,

    body: "mcb",

    actuator: "manual",

    defaultClosed: true,

    terminals: [
      ...T(["1"], "top", "phase"),
      ...T(["2"], "bottom", "phase"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "no",
        main: true,
      },
    ],

    info:
      "اضغط عليه لفتح/غلق القاطع",
  },

  {
    type: "mcb_3p",
    name: "MCB ثلاثي الطور",
    group: "breakers",

    w: 150,
    h: 130,

    body: "mcb",

    actuator: "manual",

    defaultClosed: true,

    terminals: [
      ...T(["1", "3", "5"], "top", "phase"),
      ...T(["2", "4", "6"], "bottom", "phase"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "no",
        main: true,
      },
      {
        a: "3",
        b: "4",
        type: "no",
        main: true,
      },
      {
        a: "5",
        b: "6",
        type: "no",
        main: true,
      },
    ],

    info:
      "علامات الأطراف القياسية: دخول 1/3/5 – خروج 2/4/6",
  },

  {
    type: "mccb_3p",
    name: "MCCB ثلاثي الطور",
    group: "breakers",

    w: 165,
    h: 150,

    body: "mccb",

    actuator: "manual",

    defaultClosed: true,

    terminals: [
      ...T(["1", "3", "5"], "top", "phase"),
      ...T(["2", "4", "6"], "bottom", "phase"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "no",
        main: true,
      },
      {
        a: "3",
        b: "4",
        type: "no",
        main: true,
      },
      {
        a: "5",
        b: "6",
        type: "no",
        main: true,
      },
    ],

    info:
      "علامات الأطراف القياسية: دخول 1/3/5 – خروج 2/4/6",
  },

  // =====================================================
  // RCCB / RCBO
  // =====================================================

  {
    type: "rccb",
    name: "RCCB قاطع تسرب أرضي",
    group: "rccb",

    w: 140,
    h: 140,

    body: "rccb",

    actuator: "manual",

    defaultClosed: true,

    terminals: [
      ...T(["L", "N"], "top", "phase"),
      ...T(["T", "TN"], "bottom", "phase"),
    ],

    poles: [
      {
        a: "L",
        b: "T",
        type: "no",
        main: true,
      },
      {
        a: "N",
        b: "TN",
        type: "no",
        main: true,
      },
    ],
  },

  {
    type: "rcbo",
    name: "RCBO",
    group: "rccb",

    w: 120,
    h: 140,

    body: "rccb",

    actuator: "manual",

    defaultClosed: true,

    terminals: [
      ...T(["L", "N"], "top", "phase"),
      ...T(["T", "TN"], "bottom", "phase"),
    ],

    poles: [
      {
        a: "L",
        b: "T",
        type: "no",
        main: true,
      },
      {
        a: "N",
        b: "TN",
        type: "no",
        main: true,
      },
    ],
  },

  // =====================================================
  // OVERLOAD / FUSE
  // =====================================================

  {
    type: "overload",
    name: "Overload Relay",
    group: "overload",

    // Schneider TeSys LRD06 الحقيقي المرسل من المستخدم.
    // الصورة مقصوصة على حدود الجهاز وبدون خلفية بيضاء.
    w: 180,
    h: 250,

    body: "overload",

    actuator: "trip",

    terminals: [
      ...T(["L1", "L2", "L3"], "top", "phase"),
      ...T(["T1", "T2", "T3"], "bottom", "phase"),
      ...T(["95", "97"], "left", "control"),
      ...T(["96", "98"], "right", "control"),
    ],

    poles: [
      {
        a: "L1",
        b: "T1",
        type: "nc",
        main: true,
      },
      {
        a: "L2",
        b: "T2",
        type: "nc",
        main: true,
      },
      {
        a: "L3",
        b: "T3",
        type: "nc",
        main: true,
      },
      {
        a: "95",
        b: "96",
        type: "nc",
      },
      {
        a: "97",
        b: "98",
        type: "no",
      },
    ],

    info:
      "95/96 نقطة NC للحماية – 97/98 نقطة إنذار NO – اضغط عليه لمحاكاة الفصل",
  },

  {
    type: "fuse",
    name: "Fuse فيوز",
    group: "overload",

    w: 80,
    h: 120,

    body: "fuse",

    actuator: "trip",

    terminals: [
      ...T(["1"], "top", "phase"),
      ...T(["2"], "bottom", "phase"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "nc",
        main: true,
      },
    ],
  },

  // =====================================================
  // PHASE PROTECTION
  // =====================================================

  {
    type: "phase_failure",
    name: "Phase Failure Relay",
    group: "phase",

    // صورة SKAD 04 الجديدة: نفس نسبة الصورة الأصلية 1024×1536.
    w: 200,
    h: 350,

    body: "monitor",
    actuator: "none",

    // إعدادات افتراضية قابلة للتعديل من الخصائص.
    protectionMinVoltage: 340,
    protectionMaxVoltage: 440,
    protectionDelay: 3,

    // حسب تحديد المستخدم:
    // التغذية: N + L1 + L2 + L3
    // 2 = COM ، 3 = NO ، 1 = NC
    terminals: [
      ...T(["3", "2", "1"], "top", "control", ["NO 3", "COM 2", "NC 1"]),
      ...T(["N"], "bottom", "neutral"),
      ...T(["L1", "L2", "L3"], "bottom", "phase"),
    ],

    poles: [
      { a: "2", b: "3", type: "no" },
      { a: "2", b: "1", type: "nc" },
    ],

    info:
      "Phase Failure Relay: N/L1/L2/L3 – 2 COM – 3 NO – 1 NC",
  },

  
  {
    type: "voltage_monitor",
    name: "حماية ارتفاع/انخفاض الفولتية",
    group: "phase",

    // صورة SKAD 0 الجديدة: نفس نسبة الصورة الأصلية 1024×1536.
    w: 200,
    h: 350,

    body: "monitor",
    actuator: "none",

    // إعدادات افتراضية قابلة للتعديل من الخصائص.
    protectionMinVoltage: 190,
    protectionMaxVoltage: 250,
    protectionDelay: 3,

    // حسب تحديد المستخدم:
    // التغذية: N + L
    // 2 = COM ، 3 = NO ، 1 = NC
    terminals: [
      ...T(["3", "2", "1"], "top", "control", ["NO 3", "COM 2", "NC 1"]),
      ...T(["N"], "bottom", "neutral"),
      ...T(["L"], "bottom", "phase"),
    ],

    poles: [
      { a: "2", b: "3", type: "no" },
      { a: "2", b: "1", type: "nc" },
    ],

    info:
      "حماية ارتفاع/انخفاض الفولتية: N/L – 2 COM – 3 NO – 1 NC",
  },

  // =====================================================
  // TERMINALS
  // =====================================================

  {
    type: "terminal_block",
    name: "Terminal Block",
    group: "terminals",

    w: 130,
    h: 70,

    body: "tb",

    actuator: "none",

    terminals: [
      ...T(["1", "2", "3", "4"], "top", "phase"),
      ...T(
        ["1b", "2b", "3b", "4b"],
        "bottom",
        "phase",
        ["1", "2", "3", "4"],
      ),
    ],

    links: [
      ["1", "1b"],
      ["2", "2b"],
      ["3", "3b"],
      ["4", "4b"],
    ],
  },

  {
    type: "neutral_terminal",
    name: "Neutral Terminal",
    group: "terminals",

    w: 140,
    h: 60,

    body: "tb_n",

    actuator: "none",

    terminals: [
      ...T(
        ["N1", "N2", "N3", "N4"],
        "top",
        "neutral",
      ),
    ],

    links: [
      ["N1", "N2"],
      ["N2", "N3"],
      ["N3", "N4"],
    ],
  },

  {
    type: "earth_terminal",
    name: "Earth Terminal PE",
    group: "terminals",

    w: 140,
    h: 60,

    body: "tb_pe",

    actuator: "none",

    terminals: [
      ...T(
        ["PE1", "PE2", "PE3", "PE4"],
        "top",
        "earth",
      ),
    ],

    links: [
      ["PE1", "PE2"],
      ["PE2", "PE3"],
      ["PE3", "PE4"],
    ],
  },

  {
    type: "busbar",
    name: "Busbar باسبار",
    group: "terminals",

    w: 260,
    h: 46,

    body: "busbar",

    actuator: "none",

    terminals: [
      ...T(
        ["B1", "B2", "B3", "B4", "B5", "B6"],
        "bottom",
        "phase",
      ),
    ],

    links: [
      ["B1", "B2"],
      ["B2", "B3"],
      ["B3", "B4"],
      ["B4", "B5"],
      ["B5", "B6"],
    ],
  },

  // =====================================================
  // SOURCES
  // =====================================================

  {
    type: "src_3ph",
    name: "تغذية ثلاثية 380/400V",
    group: "sources",

    w: 175,
    h: 120,

    body: "src3",

    actuator: "none",

    // فولتية خط-إلى-خط الافتراضية؛ يمكن تغييرها من السلايدر في الخصائص.
    sourceVoltage: 400,

    terminals: [
      ...T(
        ["L1", "L2", "L3", "N", "PE"],
        "bottom",
        "phase",
      ),
    ],

    rails: [
      {
        t: "L1",
        rail: "L1",
      },
      {
        t: "L2",
        rail: "L2",
      },
      {
        t: "L3",
        rail: "L3",
      },
      {
        t: "N",
        rail: "N",
      },
      {
        t: "PE",
        rail: "PE",
      },
    ],
  },

  {
    type: "src_1ph",
    name: "تغذية أحادية 220/230V",
    group: "sources",

    w: 140,
    h: 110,

    body: "src1",

    actuator: "none",

    // فولتية فاز-إلى-نيوترال الافتراضية؛ يمكن تغييرها من السلايدر في الخصائص.
    sourceVoltage: 230,

    terminals: [
      ...T(["L", "N", "PE"], "bottom", "phase"),
    ],

    rails: [
      {
        t: "L",
        rail: "L1",
      },
      {
        t: "N",
        rail: "N",
      },
      {
        t: "PE",
        rail: "PE",
      },
    ],
  },

  {
    type: "src_ctrl_220",
    name: "تغذية سيطرة 220V",
    group: "sources",

    w: 140,
    h: 100,

    body: "srcc",

    actuator: "none",

    terminals: [
      ...T(["L", "N"], "bottom", "control"),
    ],

    rails: [
      {
        t: "L",
        rail: "L1",
      },
      {
        t: "N",
        rail: "N",
      },
    ],
  },

  {
    type: "src_ctrl_24",
    name: "تغذية سيطرة 24V DC",
    group: "sources",

    w: 140,
    h: 100,

    body: "srcc",

    actuator: "none",

    terminals: [
      ...T(["+24", "0V"], "bottom", "control"),
    ],

    rails: [
      {
        t: "+24",
        rail: "C+",
      },
      {
        t: "0V",
        rail: "C-",
      },
    ],
  },

  // =====================================================
  // LOADS
  // =====================================================

  {
    type: "motor_3ph",
    name: "محرك ثلاثي الطور",
    group: "loads",

    w: 160,
    h: 140,

    body: "motor3",

    actuator: "none",

    terminals: [
      ...T(["U1", "V1", "W1"], "top", "load"),
      ...T(["PE"], "bottom", "earth"),
    ],

    load: {
      kind: "3ph",
      terminals: ["U1", "V1", "W1"],
      visual: "motor",
    },

    info:
      "علبة أطراف المحرك: U1/V1/W1 + أرضي PE",
  },

  {
    type: "motor_3ph_6t",
    name: "محرك ثلاثي الطور 6 أطراف Star / Delta",
    group: "loads",

    w: 180,
    h: 160,

    body: "motor3",

    actuator: "none",

    terminals: [
      ...T(["U1", "V1", "W1"], "top", "load"),
      ...T(["U2", "V2", "W2"], "bottom", "load"),
      ...T(["PE"], "right", "earth"),
    ],

    load: {
      kind: "3ph",
      terminals: ["U1", "V1", "W1"],
      visual: "motor",
    },

    info:
      "محرك ثلاثي الطور بستة أطراف U1/V1/W1 و U2/V2/W2 مخصص لدوائر Star / Delta",
  },

  {
    type: "motor_1ph",
    name: "محرك أحادي الطور",
    group: "loads",

    w: 140,
    h: 130,

    body: "motor1",

    actuator: "none",

    terminals: [
      ...T(["L", "N"], "top", "load"),
      ...T(["PE"], "bottom", "earth"),
    ],

    load: {
      kind: "1ph",
      terminals: ["L", "N"],
      visual: "motor",
    },
  },

  {
    type: "lamp",
    name: "مصباح إشارة Lamp",
    group: "loads",

    w: 100,
    h: 110,

    body: "lamp",

    actuator: "none",

    terminals: [
      ...T(["X1"], "left", "load"),
      ...T(["X2"], "right", "load"),
    ],

    load: {
      kind: "1ph",
      terminals: ["X1", "X2"],
      visual: "lamp",
    },
  },

  {
    type: "fan",
    name: "مروحة Fan",
    group: "loads",

    w: 130,
    h: 130,

    body: "fan",

    actuator: "none",

    terminals: [
      ...T(["L", "N"], "top", "load"),
    ],

    load: {
      kind: "1ph",
      terminals: ["L", "N"],
      visual: "fan",
    },
  },

  {
    type: "fan_dc",
    name: "فان DC",
    group: "loads",

    w: 130,
    h: 130,

    body: "fan",

    actuator: "none",

    terminals: [
      ...T(["DC+", "DC-"], "top", "load"),
    ],

    load: {
      kind: "dc",
      terminals: ["DC+", "DC-"],
      visual: "fan",
    },

    info:
      "فان 24V DC – يعمل على C+/C- ويعكس اتجاه الدوران عند عكس القطبية.",
  },

  {
    type: "ac_split",
    name: "مكيف سبليت Split AC",
    group: "loads",

    w: 190,
    h: 120,

    body: "ac",

    actuator: "none",

    terminals: [
      ...T(["L", "N"], "top", "load"),
      ...T(["PE"], "bottom", "earth"),
    ],

    load: {
      kind: "1ph",
      terminals: ["L", "N"],
      visual: "ac",
    },
  },

  {
    type: "solenoid",
    name: "صمام كهربائي Solenoid Valve",
    group: "loads",

    w: 130,
    h: 110,

    body: "solenoid",

    actuator: "none",

    terminals: [
      ...T(["A1"], "left", "load"),
      ...T(["A2"], "right", "load"),
    ],

    load: {
      kind: "1ph",
      terminals: ["A1", "A2"],
      visual: "solenoid",
    },

    info:
      "ملف الصمام A1/A2 – يفتح عند التغذية",
  },

  {
    type: "heater",
    name: "سخّان Heater",
    group: "loads",

    w: 150,
    h: 110,

    body: "heater",

    actuator: "none",

    terminals: [
      ...T(["L", "N"], "top", "load"),
      ...T(["PE"], "bottom", "earth"),
    ],

    load: {
      kind: "1ph",
      terminals: ["L", "N"],
      visual: "heater",
    },
  },

  {
    type: "heater_3ph",
    name: "سخّان ثلاثي الطور",
    group: "loads",

    w: 170,
    h: 115,

    body: "heater",

    actuator: "none",

    terminals: [
      ...T(["L1", "L2", "L3"], "top", "load"),
      ...T(["PE"], "bottom", "earth"),
    ],

    load: {
      kind: "3ph",
      terminals: ["L1", "L2", "L3"],
      visual: "heater",
    },
  },

  {
    type: "brake",
    name: "فرامل كهرومغناطيسية Brake",
    group: "loads",

    w: 140,
    h: 110,

    body: "solenoid",

    actuator: "none",

    terminals: [
      ...T(["A1"], "left", "load"),
      ...T(["A2"], "right", "load"),
    ],

    load: {
      kind: "1ph",
      terminals: ["A1", "A2"],
      visual: "solenoid",
    },
  },

  {
    type: "pump_3ph",
    name: "مضخة ماء ثلاثية الطور",
    group: "loads",

    w: 175,
    h: 130,

    body: "motor3",

    actuator: "none",

    terminals: [
      ...T(["U1", "V1", "W1"], "top", "load"),
      ...T(["PE"], "bottom", "earth"),
    ],

    load: {
      kind: "3ph",
      terminals: ["U1", "V1", "W1"],
      visual: "motor",
    },
  },

  // =====================================================
  // METERS / SIGNALS
  // =====================================================

  {
    type: "buzzer",
    name: "جرس / صفارة Buzzer",
    group: "meters",

    w: 110,
    h: 100,

    body: "monitor",

    actuator: "none",

    terminals: [
      ...T(["X1"], "left", "load"),
      ...T(["X2"], "right", "load"),
    ],

    load: {
      kind: "1ph",
      terminals: ["X1", "X2"],
      visual: "buzzer",
    },
  },

  {
    type: "pilot_red",
    name: "لمبة إشارة حمراء",
    group: "meters",

    w: 95,
    h: 95,

    body: "lamp",

    actuator: "none",

    terminals: [
      ...T(["X1"], "left", "load"),
      ...T(["X2"], "right", "load"),
    ],

    load: {
      kind: "1ph",
      terminals: ["X1", "X2"],
      visual: "lamp",
    },
  },

  {
    type: "pilot_green",
    name: "لمبة إشارة خضراء",
    group: "meters",

    w: 95,
    h: 95,

    body: "lamp",

    actuator: "none",

    terminals: [
      ...T(["X1"], "left", "load"),
      ...T(["X2"], "right", "load"),
    ],

    load: {
      kind: "1ph",
      terminals: ["X1", "X2"],
      visual: "lamp",
    },
  },

  {
    type: "pilot_amber",
    name: "لمبة إشارة صفراء",
    group: "meters",

    w: 95,
    h: 95,

    body: "lamp",

    actuator: "none",

    terminals: [
      ...T(["X1"], "left", "load"),
      ...T(["X2"], "right", "load"),
    ],

    load: {
      kind: "1ph",
      terminals: ["X1", "X2"],
      visual: "lamp",
    },
  },

  /* =====================================================
     BLUE PILOT LAMP
     ===================================================== */

  {
    type: "pilot_blue",
    name: "لمبة إشارة زرقاء",
    group: "meters",

    w: 95,
    h: 95,

    body: "lamp",

    actuator: "none",

    terminals: [
      ...T(["X1"], "left", "load"),
      ...T(["X2"], "right", "load"),
    ],

    load: {
      kind: "1ph",
      terminals: ["X1", "X2"],
      visual: "lamp",
    },

    info:
      "لمبة إشارة زرقاء تضيء عند وجود التغذية بين X1 و X2",
  },

  {
    type: "voltmeter",
    name: "فولتميتر Voltmeter",
    group: "meters",

    w: 120,
    h: 110,

    body: "monitor",

    actuator: "none",

    meter: "volt",

    terminals: [
      ...T(["V1"], "left", "load"),
      ...T(["V2"], "right", "load"),
    ],

    info:
      "يقيس الفولتية بين طرفيه V1/V2",
  },

  {
    type: "ammeter",
    name: "أميتر Ammeter",
    group: "meters",

    w: 120,
    h: 110,

    body: "monitor",

    actuator: "none",

    meter: "amp",

    terminals: [
      ...T(["1"], "top", "load"),
      ...T(["2"], "bottom", "load"),
    ],

    links: [
      ["1", "2"],
    ],

    info:
      "يوصل على التوالي مع الحمل",
  },

  {
    type: "multimeter_panel",
    name: "جهاز قياس متعدد Multi-Meter",
    group: "meters",

    w: 150,
    h: 130,

    body: "monitor",

    actuator: "none",

    meter: "multi",

    terminals: [
      ...T(["L1", "L2", "L3"], "top", "phase"),
      ...T(["N"], "bottom", "load"),
    ],

    info:
      "يقرأ فولتية الفازات والتردد",
  },

  // =====================================================
  // MPCB - ستارتر حراري
  // مختلف عن MCCB
  // =====================================================

  {
    type: "mpcb",
    name: "MPCB - ستارتر حراري",
    group: "breakers",

    w: 150,
    h: 130,

    /*
      مهم:
      هذا مو MCCB.
      نخليه body مستقل حتى DeviceView يرسمه
      كستارتر حراري حقيقي.
    */
    body: "mpcb",

    actuator: "manual",

    terminals: [
      ...T(["L1", "L2", "L3"], "top", "phase"),
      ...T(["T1", "T2", "T3"], "bottom", "phase"),
    ],

    poles: [
      {
        a: "L1",
        b: "T1",
        type: "no",
        main: true,
      },
      {
        a: "L2",
        b: "T2",
        type: "no",
        main: true,
      },
      {
        a: "L3",
        b: "T3",
        type: "no",
        main: true,
      },
    ],

    info:
      "ستارتر حراري / قاطع حماية محرك قابل لضبط تيار الحماية (GV2 / MS132)",
  },

  // =====================================================
  // ISOLATOR
  // =====================================================

  {
    type: "isolator_3p",
    name: "مفتاح عزل رئيسي Isolator",
    group: "dist",

    w: 160,
    h: 125,

    body: "mccb",

    actuator: "manual",

    terminals: [
      ...T(["L1", "L2", "L3"], "top", "phase"),
      ...T(["T1", "T2", "T3"], "bottom", "phase"),
    ],

    poles: [
      {
        a: "L1",
        b: "T1",
        type: "no",
        main: true,
      },
      {
        a: "L2",
        b: "T2",
        type: "no",
        main: true,
      },
      {
        a: "L3",
        b: "T3",
        type: "no",
        main: true,
      },
    ],

    info:
      "مفتاح فصل يدوي رئيسي (يبقى بالوضعية)",
  },

  // =====================================================
  // CHANGEOVER
  // =====================================================

  {
    type: "changeover",
    name: "مفتاح تحويل Changeover 1-0-2",
    group: "dist",

    w: 165,
    h: 120,

    body: "mccb",

    actuator: "select",

    positions: [
      "مصدر 1",
      "0",
      "مصدر 2",
    ],

    terminals: [
      ...T(["1", "3"], "top", "phase"),
      ...T(["2"], "bottom", "phase"),
    ],

    poles: [
      {
        a: "1",
        b: "2",
        type: "no",
        pos: [0],
      },
      {
        a: "3",
        b: "2",
        type: "no",
        pos: [2],
      },
    ],

    info:
      "يحوّل الخرج بين مصدرين ولا يوصلهما معاً",
  },
];

/* =========================================================
   SPECS MAP
   ========================================================= */

export const SPECS: Record<
  string,
  DeviceSpec
> = Object.fromEntries(
  specs.map((s) => [
    s.type,
    s,
  ]),
);

export const ALL_SPECS =
  specs;

/* =========================================================
   TERMINAL POSITION
   ========================================================= */

const PAD = 16;

export function terminalLocal(
  spec: DeviceSpec,
  t: TerminalSpec,
) {
  // Schneider RXM 14-pin: use the centres of the ORIGINAL numbered pads in the user's image.
  // No invented 1..14 terminal row is drawn around the relay.
  if (spec.type === "relay") {
    const raw: Record<string, { x: number; y: number }> = {
      "1": { x: 870, y: 170 }, "2": { x: 670, y: 170 }, "3": { x: 470, y: 165 },
      "8": { x: 265, y: 320 }, "7": { x: 461, y: 320 }, "6": { x: 665, y: 320 }, "5": { x: 870, y: 320},
      "12": { x: 250, y: 1045 }, "11": { x: 447, y: 1050 }, "10": { x: 655, y: 1050 }, "9": { x: 855, y: 1050 },
      "4": { x: 250, y: 1217 }, "14": { x: 448, y: 1219 }, "13": { x: 643, y: 1220 },
    };
    const q = raw[t.id];
    if (q) return { x: spec.w * (q.x / 1000), y: spec.h * (q.y / 1407) };
  }
  // =====================================================
  // TIMER PF083A - ضبط نقاط التايمر نقطة نقطة
  // =====================================================
  // الصورة الأصلية: 1199 × 1312
  // ترتيب الأطراف كما حدد المستخدم:
  // فوق: 6 - 5 - 4 - 3
  // جوه: 7 - 8 - 1 - 2
  //
  // إذا تريد تحرك أي نقطة لاحقاً غيّر x/y داخل timerPointAdjust فقط:
  // x موجب = يمين | x سالب = يسار
  // y موجب = جوه   | y سالب = فوك
  //
  // مهم: لأن الضبط هنا داخل terminalLocal، نقطة السلك + دائرة التحديد +
  // منطقة الضغط كلها تتحرك سوية، وما ينفصل السلك عن البرغي.
  if (spec.type === "timer" || spec.type === "timer_off") {
    const raw: Record<string, { x: number; y: number }> = {
      "6": { x: 271, y: 224 },
      "5": { x: 486, y: 224 },
      "4": { x: 698, y: 224 },
      "3": { x: 910, y: 224 },
      "7": { x: 271, y: 1031 },
      "8": { x: 486, y: 1031 },
      "1": { x: 698, y: 1031 },
      "2": { x: 910, y: 1031 },
    };

    const timerPointAdjust: Record<string, { x: number; y: number }> = {
      "1": { x: 1, y: 4 },
      "2": { x: 2, y: 4 },
      "3": { x: 2, y: 0 },
      "4": { x: 1.5, y: 0 },
      "5": { x: 0, y: 0 },
      "6": { x: 0, y: 0 },
      "7": { x: 1, y: 4 },
      "8": { x: 0.5, y: 4 },
    };

    const q = raw[t.id];
    if (q) {
      const adj = timerPointAdjust[t.id] ?? { x: 0, y: 0 };
      return {
        x: spec.w * (q.x / 1199) + adj.x,
        y: spec.h * (q.y / 1312) + adj.y,
      };
    }
  }

  // =====================================================
  // PHASE FAILURE / VOLTAGE MONITOR - ضبط النقاط نقطة نقطة
  // =====================================================
  // الصورتان الأصليتان: 1024 × 1536
  //
  // إذا احتجت تعديل يدوي لاحقاً غيّر فقط قيم x/y داخل الجداول أدناه:
  // x موجب = يمين | x سالب = يسار
  // y موجب = جوه   | y سالب = فوك
  //
  // النقطة + منطقة الضغط + نهاية السلك كلها تعتمد terminalLocal نفسه،
  // لذلك أي تعديل هنا يحركها سوية 100%.

  if (spec.type === "phase_failure") {
    const raw: Record<string, { x: number; y: number }> = {
      "3": { x: 381, y: 121 },
      "2": { x: 466, y: 121 },
      "1": { x: 553, y: 122 },

      "N":  { x: 381, y: 1333 },
      "L1": { x: 466, y: 1335 },
      "L2": { x: 554, y: 1334 },
      "L3": { x: 640, y: 1335 },
    };

    const phaseFailurePointAdjust: Record<string, { x: number; y: number }> = {
      "1": { x: 0, y: 0 },
      "2": { x: 0, y: 0 },
      "3": { x: 0, y: 0 },
      "N": { x: 0, y: 0 },
      "L1": { x: 0, y: 0 },
      "L2": { x: 0, y: 0 },
      "L3": { x: 0, y: 0 },
    };

    const q = raw[t.id];
    if (q) {
      const adj = phaseFailurePointAdjust[t.id] ?? { x: 0, y: 0 };
      return {
        x: spec.w * (q.x / 1024) + adj.x,
        y: spec.h * (q.y / 1536) + adj.y,
      };
    }
  }

  if (spec.type === "voltage_monitor") {
    const raw: Record<string, { x: number; y: number }> = {
      "3": { x: 381, y: 121 },
      "2": { x: 464, y: 122 },
      "1": { x: 553, y: 122 },

      "N": { x: 381, y: 1335 },
      "L": { x: 467, y: 1336 },
    };

    const voltageMonitorPointAdjust: Record<string, { x: number; y: number }> = {
      "1": { x: 0, y: 0 },
      "2": { x: 0, y: 0 },
      "3": { x: 0, y: 0 },
      "N": { x: 0, y: 0 },
      "L": { x: 0, y: 0 },
    };

    const q = raw[t.id];
    if (q) {
      const adj = voltageMonitorPointAdjust[t.id] ?? { x: 0, y: 0 };
      return {
        x: spec.w * (q.x / 1024) + adj.x,
        y: spec.h * (q.y / 1536) + adj.y,
      };
    }
  }

  // الكونتكتر الرباعي LC1DT40: مراكز البراغي مقاسة مباشرة من صورة المستخدم 744×1536.
  if (spec.type === "contactor_small") {
    const sx = spec.w / 744;
    const sy = spec.h / 1536;
    const raw: Record<string, { x: number; y: number }> = {
      L1: { x: 112.2, y: 282.6 }, L2: { x: 282.6, y: 281.4 },
      L3: { x: 450.6, y: 279.0 }, L4: { x: 619.8, y: 279.0 },
      "13": { x: 285.0, y: 556.2 }, "21": { x: 456.6, y: 555.0 }, A1: { x: 629.4, y: 556.2 },
      "14": { x: 285.0, y: 1189.8 }, "22": { x: 456.6, y: 1192.2 }, A2: { x: 628.2, y: 1183.8 },
      T1: { x: 119.4, y: 1377.0 }, T2: { x: 283.8, y: 1383.0 },
      T3: { x: 450.6, y: 1380.6 }, T4: { x: 618.6, y: 1375.8 },
    };
    const q = raw[t.id];
    if (q) {
      const p = { x: q.x * sx, y: q.y * sy };
      const inward = t.side === "top" ? { x: 0, y: 6 } : t.side === "bottom" ? { x: 0, y: -6 } : t.side === "left" ? { x: 6, y: 0 } : { x: -6, y: 0 };
      return { x: p.x + inward.x, y: p.y + inward.y };
    }
  }

  // أوكسيلاري LADN13: مراكز البراغي مقاسة مباشرة من صورة المستخدم 1254×1254.
  if (spec.type === "aux_block") {
    const sx = spec.w / 1254;
    const sy = spec.h / 1254;
    const raw: Record<string, { x: number; y: number }> = {
      "53": { x: 204.6, y: 292.2 }, "61": { x: 490.2, y: 292.2 },
      "71": { x: 775.8, y: 292.2 }, "81": { x: 1053.0, y: 298.2 },
      "54": { x: 203.4, y: 987.0 }, "62": { x: 490.2, y: 988.2 },
      "72": { x: 784.2, y: 973.8 }, "82": { x: 1050.6, y: 991.8 },
    };
    const q = raw[t.id];
    if (q) {
      const p = { x: q.x * sx, y: q.y * sy };
      const inward = t.side === "top" ? { x: 0, y: 6 } : { x: 0, y: -6 };
      return { x: p.x + inward.x, y: p.y + inward.y };
    }
  }

  // Schneider TeSys LRD06: الصورة المرجعية النهائية 828×902 التي اعتمدها المستخدم.
  // النقاط هنا هي مراكز الدوائر الخضراء الظاهرة في الصورة نفسها، حتى تكون
  // نقطة الضغط ونهاية السلك مطابقتين بصرياً 100% للدائرة/البرغي.
  if (spec.type === "overload") {
    const sx = 180 / 828;
    const sy = 250 / 902;
    const screwCenters: Record<string, { x: number; y: number }> = {
      L1: { x: 252.78, y: 87.57 }, L2: { x: 436.44, y: 87.87 }, L3: { x: 615.59, y: 84.64 },
      "97": { x: 195.74, y: 564.37 }, "98": { x: 354.48, y: 564.32 },
      "95": { x: 515.59, y: 564.38 }, "96": { x: 677.82, y: 564.26 },
      T1: { x: 241.56, y: 721.04 }, T2: { x: 436.57, y: 720.99 }, T3: { x: 633.05, y: 720.95 },
    };
    const q = screwCenters[t.id];
    if (q) {
      const p = { x: q.x * sx, y: q.y * sy };
      const inward = t.side === "top" ? { x: 0, y: 6 } : t.side === "bottom" ? { x: 0, y: -6 } : t.side === "left" ? { x: 6, y: 0 } : { x: -6, y: 0 };
      return { x: p.x + inward.x, y: p.y + inward.y };
    }
  }

  // الكونتكتر الثلاثي LC1D25: مراكز البراغي مقاسة مباشرة من صورة المستخدم 889×1536.
  if (spec.type === "contactor") {
    const sx = spec.w / 889;
    const sy = spec.h / 1536;
    const raw: Record<string, { x: number; y: number }> = {
      L1: { x: 203.4, y: 265 }, L2: { x: 445.8, y: 265 }, L3: { x: 689.4, y: 265 },
      "13": { x: 358.2, y: 539.4 }, "21": { x: 543.0, y: 539.4 }, A1: { x: 727.8, y: 540},
      "14": { x: 364.2, y: 1207.8 }, "22": { x: 545.4, y: 1205.4 }, A2: { x: 725.4, y: 1206.6 },
      T1: { x: 204.6, y: 1370 }, T2: { x: 445.8, y: 1370}, T3: { x: 684.6, y: 1370 },
    };
    const q = raw[t.id];
    if (q) {
      const p = { x: q.x * sx, y: q.y * sy };
      const inward = t.side === "top" ? { x: 0, y: 6 } : t.side === "bottom" ? { x: 0, y: -6 } : t.side === "left" ? { x: 6, y: 0 } : { x: -6, y: 0 };
      return { x: p.x + inward.x, y: p.y + inward.y };
    }
  }

  const step = (
    n: number,
    count: number,
  ) =>
    ((n + 1) /
      (count + 1)) *
    100;

  switch (t.side) {
    case "top":
      return {
        x:
          (step(
            t.slot,
            t.slots,
          ) /
            100) *
          spec.w,

        y: 0,
      };

    case "bottom":
      return {
        x:
          (step(
            t.slot,
            t.slots,
          ) /
            100) *
          spec.w,

        y: spec.h,
      };

    case "left":
      return {
        x: 0,

        y:
          (step(
            t.slot,
            t.slots,
          ) /
            100) *
          spec.h,
      };

    default:
      return {
        x: spec.w,

        y:
          (step(
            t.slot,
            t.slots,
          ) /
            100) *
          spec.h,
      };
  }
}

/* =========================================================
   ROTATE POINT
   ========================================================= */

export function rotatePoint(
  p: {
    x: number;
    y: number;
  },

  spec: DeviceSpec,

  rot: number,
) {
  const cx =
    spec.w / 2;

  const cy =
    spec.h / 2;

  const a =
    (rot *
      Math.PI) /
    180;

  const dx =
    p.x - cx;

  const dy =
    p.y - cy;

  return {
    x:
      cx +
      dx *
        Math.cos(a) -
      dy *
        Math.sin(a),

    y:
      cy +
      dx *
        Math.sin(a) +
      dy *
        Math.cos(a),
  };
}

export const TERMINAL_PAD =
  PAD;